import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Canonical-origin guard:
// The Emergent ingress sometimes serves the SPA from a CDN-cached subdomain
// (e.g. `*.preview.static.emergentagent.com`) that is DIFFERENT from the
// backend host (`REACT_APP_BACKEND_URL`). When that happens, every API call
// becomes a cross-origin request and the upstream proxy rewrites the CORS
// `Access-Control-Allow-Origin` header to `*` while still sending
// `Allow-Credentials: true` — which browsers reject, surfacing as a generic
// "Network Error" on login. Redirect once at app boot to the canonical
// backend origin so the SPA and the API always share an origin.
let __redirecting = false;
try {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) {
    const backendOrigin = new URL(backendUrl).origin;
    if (window.location.origin !== backendOrigin) {
      const target =
        backendOrigin +
        window.location.pathname +
        window.location.search +
        window.location.hash;
      __redirecting = true;
      window.location.replace(target);
    }
  }
} catch (e) {
  // Canonical-host redirect is non-critical — booting continues even when
  // URL parsing fails (e.g. exotic browsers, file:// schemes during tests).
  // Logged so any silent boot regression surfaces in dev tools.
  console.warn("[boot] canonical-host check skipped:", e);
}

if (!__redirecting) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
    },
  });

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
